/**
 * Firebase Security Service
 * Hybrid architecture: Firebase for persistence + In-memory cache for speed
 */

import { db } from '../config/firebase.config';
import {
    collection,
    addDoc,
    getDocs,
    query,
    where,
    orderBy,
    limit,
    doc,
    serverTimestamp,
    onSnapshot,
    Timestamp,
    updateDoc,
} from 'firebase/firestore';

// Interfaces
export interface BlockedIP {
    id: string;
    ip: string;
    reason: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    blockedAt: Date;
    blockedBy: string;
    isActive: boolean;
    expiresAt?: Date;
}

export interface SecurityEvent {
    id: string;
    type: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    ip: string;
    timestamp: Date;
    message: string;
    details: string;
    resolved: boolean;
}

export interface AttackLog {
    id: string;
    attackType: string;
    ip: string;
    timestamp: Date;
    payload?: string;
    endpoint?: string;
    blocked: boolean;
    autoBlocked: boolean;
}

export interface AdminAction {
    id: string;
    adminId: string;
    action: string;
    target: string;
    timestamp: Date;
    reason?: string;
}

class FirebaseSecurityService {
    // In-memory cache for fast access
    private static cache = {
        blockedIPs: [] as BlockedIP[],
        securityEvents: [] as SecurityEvent[],
        attackLogs: [] as AttackLog[],
        lastSync: new Date(),
        isInitialized: false,
    };

    // Real-time listeners
    private static unsubscribers: (() => void)[] = [];

    /**
     * Initialize service: Load data from Firebase
     */
    static async initialize(): Promise<void> {
        if (this.cache.isInitialized) {
            console.log('✅ Firebase Security Service already initialized');
            return;
        }

        console.log('📥 Initializing Firebase Security Service...');

        try {
            // Load blocked IPs
            await this.loadBlockedIPs();

            // Load recent security events (last 7 days)
            await this.loadSecurityEvents();

            // Load recent attack logs (last 24 hours)
            await this.loadAttackLogs();

            // Set up real-time listeners
            this.setupRealTimeSync();

            this.cache.lastSync = new Date();
            this.cache.isInitialized = true;

            console.log('✅ Firebase Security Service initialized');
            console.log(`   - Blocked IPs: ${this.cache.blockedIPs.length}`);
            console.log(`   - Security Events: ${this.cache.securityEvents.length}`);
            console.log(`   - Attack Logs: ${this.cache.attackLogs.length}`);
        } catch (error) {
            console.error('❌ Failed to initialize Firebase Security Service:', error);
            throw error;
        }
    }

    /**
     * Load blocked IPs from Firebase
     */
    private static async loadBlockedIPs(): Promise<void> {
        try {
            const q = query(
                collection(db, 'blockedIPs'),
                where('isActive', '==', true),
                orderBy('blockedAt', 'desc')
            );

            const snapshot = await getDocs(q);
            this.cache.blockedIPs = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                blockedAt: (doc.data().blockedAt as Timestamp).toDate(),
                expiresAt: doc.data().expiresAt ? (doc.data().expiresAt as Timestamp).toDate() : undefined,
            })) as BlockedIP[];

            console.log(`📥 Loaded ${this.cache.blockedIPs.length} blocked IPs from Firebase`);
        } catch (error) {
            console.error('❌ Error loading blocked IPs:', error);
            this.cache.blockedIPs = [];
        }
    }

    /**
     * Load security events from Firebase
     */
    private static async loadSecurityEvents(): Promise<void> {
        try {
            const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
            const q = query(
                collection(db, 'securityEvents'),
                where('timestamp', '>', sevenDaysAgo),
                orderBy('timestamp', 'desc'),
                limit(500)
            );

            const snapshot = await getDocs(q);
            this.cache.securityEvents = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                timestamp: (doc.data().timestamp as Timestamp).toDate(),
            })) as SecurityEvent[];

            console.log(`📥 Loaded ${this.cache.securityEvents.length} security events from Firebase`);
        } catch (error) {
            console.error('❌ Error loading security events:', error);
            this.cache.securityEvents = [];
        }
    }

    /**
     * Load attack logs from Firebase
     */
    private static async loadAttackLogs(): Promise<void> {
        try {
            const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
            const q = query(
                collection(db, 'attackLogs'),
                where('timestamp', '>', oneDayAgo),
                orderBy('timestamp', 'desc'),
                limit(1000)
            );

            const snapshot = await getDocs(q);
            this.cache.attackLogs = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                timestamp: (doc.data().timestamp as Timestamp).toDate(),
            })) as AttackLog[];

            console.log(`📥 Loaded ${this.cache.attackLogs.length} attack logs from Firebase`);
        } catch (error) {
            console.error('❌ Error loading attack logs:', error);
            this.cache.attackLogs = [];
        }
    }

    /**
     * Set up real-time sync with Firebase
     */
    private static setupRealTimeSync(): void {
        // Listen for blocked IPs changes
        const blockedIPsUnsubscribe = onSnapshot(
            query(collection(db, 'blockedIPs'), where('isActive', '==', true)),
            (snapshot) => {
                snapshot.docChanges().forEach((change) => {
                    const data = {
                        id: change.doc.id,
                        ...change.doc.data(),
                        blockedAt: (change.doc.data().blockedAt as Timestamp).toDate(),
                        expiresAt: change.doc.data().expiresAt ? (change.doc.data().expiresAt as Timestamp).toDate() : undefined,
                    } as BlockedIP;

                    if (change.type === 'added') {
                        // Check if already in cache
                        if (!this.cache.blockedIPs.find(ip => ip.id === data.id)) {
                            this.cache.blockedIPs.unshift(data);
                            console.log('🔄 New blocked IP synced:', data.ip);
                        }
                    } else if (change.type === 'modified') {
                        const index = this.cache.blockedIPs.findIndex(ip => ip.id === data.id);
                        if (index !== -1) {
                            this.cache.blockedIPs[index] = data;
                            console.log('🔄 Blocked IP updated:', data.ip);
                        }
                    } else if (change.type === 'removed') {
                        this.cache.blockedIPs = this.cache.blockedIPs.filter(ip => ip.id !== data.id);
                        console.log('🔄 Blocked IP removed:', data.ip);
                    }
                });
            }
        );

        // Listen for security events changes
        const eventsUnsubscribe = onSnapshot(
            query(
                collection(db, 'securityEvents'),
                orderBy('timestamp', 'desc'),
                limit(100)
            ),
            (snapshot) => {
                snapshot.docChanges().forEach((change) => {
                    if (change.type === 'added') {
                        const data = {
                            id: change.doc.id,
                            ...change.doc.data(),
                            timestamp: (change.doc.data().timestamp as Timestamp).toDate(),
                        } as SecurityEvent;

                        // Check if already in cache
                        if (!this.cache.securityEvents.find(e => e.id === data.id)) {
                            this.cache.securityEvents.unshift(data);
                            console.log('🔄 New security event synced:', data.type);
                        }
                    }
                });
            }
        );

        this.unsubscribers.push(blockedIPsUnsubscribe, eventsUnsubscribe);
        console.log('🔄 Real-time sync enabled');
    }

    /**
     * Block an IP address
     */
    static async blockIP(
        ip: string,
        reason: string,
        severity: 'low' | 'medium' | 'high' | 'critical',
        blockedBy: string,
        expiresInDays?: number
    ): Promise<{ success: boolean; message: string; id?: string }> {
        try {
            // Check if already blocked
            if (this.cache.blockedIPs.some(blocked => blocked.ip === ip && blocked.isActive)) {
                return { success: false, message: `IP ${ip} is already blocked` };
            }

            // Calculate expiry date if specified
            const expiresAt = expiresInDays
                ? new Date(Date.now() + expiresInDays * 24 * 60 * 60 * 1000)
                : undefined;

            // Save to Firebase
            const docRef = await addDoc(collection(db, 'blockedIPs'), {
                ip,
                reason,
                severity,
                blockedAt: serverTimestamp(),
                blockedBy,
                isActive: true,
                ...(expiresAt && { expiresAt }),
            });

            // Update cache immediately (real-time listener will also update)
            const blockedIP: BlockedIP = {
                id: docRef.id,
                ip,
                reason,
                severity,
                blockedAt: new Date(),
                blockedBy,
                isActive: true,
                ...(expiresAt && { expiresAt }),
            };
            this.cache.blockedIPs.unshift(blockedIP);

            // Log admin action
            await this.logAdminAction(blockedBy, 'blocked_ip', ip, reason);

            console.log('🚫 IP blocked and saved to Firebase:', ip);
            return { success: true, message: `IP ${ip} blocked successfully`, id: docRef.id };
        } catch (error) {
            console.error('❌ Error blocking IP:', error);
            return { success: false, message: 'Failed to block IP' };
        }
    }

    /**
     * Unblock an IP address
     */
    static async unblockIP(ip: string, unblockedBy: string): Promise<{ success: boolean; message: string }> {
        try {
            const blocked = this.cache.blockedIPs.find(b => b.ip === ip && b.isActive);
            if (!blocked) {
                return { success: false, message: `IP ${ip} is not blocked` };
            }

            // Update in Firebase (set isActive to false)
            await updateDoc(doc(db, 'blockedIPs', blocked.id), {
                isActive: false,
                unblockedAt: serverTimestamp(),
                unblockedBy,
            });

            // Update cache
            this.cache.blockedIPs = this.cache.blockedIPs.filter(b => b.id !== blocked.id);

            // Log admin action
            await this.logAdminAction(unblockedBy, 'unblocked_ip', ip);

            console.log('✅ IP unblocked and updated in Firebase:', ip);
            return { success: true, message: `IP ${ip} unblocked successfully` };
        } catch (error) {
            console.error('❌ Error unblocking IP:', error);
            return { success: false, message: 'Failed to unblock IP' };
        }
    }

    /**
     * Check if an IP is blocked (fast - from cache)
     */
    static isIPBlocked(ip: string): boolean {
        return this.cache.blockedIPs.some(blocked => blocked.ip === ip && blocked.isActive);
    }

    /**
     * Get all blocked IPs (from cache)
     */
    static getBlockedIPs(): BlockedIP[] {
        return [...this.cache.blockedIPs];
    }

    /**
     * Add a security event
     */
    static async addSecurityEvent(
        type: string,
        severity: 'low' | 'medium' | 'high' | 'critical',
        ip: string,
        message: string,
        details: string
    ): Promise<{ success: boolean; id?: string }> {
        try {
            const docRef = await addDoc(collection(db, 'securityEvents'), {
                type,
                severity,
                ip,
                message,
                details,
                timestamp: serverTimestamp(),
                resolved: false,
            });

            // Update cache
            const event: SecurityEvent = {
                id: docRef.id,
                type,
                severity,
                ip,
                message,
                details,
                timestamp: new Date(),
                resolved: false,
            };
            this.cache.securityEvents.unshift(event);

            console.log('📝 Security event saved to Firebase:', type);
            return { success: true, id: docRef.id };
        } catch (error) {
            console.error('❌ Error adding security event:', error);
            return { success: false };
        }
    }

    /**
     * Get security events (from cache)
     */
    static getSecurityEvents(): SecurityEvent[] {
        return [...this.cache.securityEvents];
    }

    /**
     * Get security statistics
     */
    static getSecurityStatistics() {
        const events = this.cache.securityEvents;
        return {
            total: events.length,
            critical: events.filter(e => e.severity === 'critical').length,
            high: events.filter(e => e.severity === 'high').length,
            medium: events.filter(e => e.severity === 'medium').length,
            low: events.filter(e => e.severity === 'low').length,
            unresolved: events.filter(e => !e.resolved).length,
        };
    }

    /**
     * Log an attack
     */
    static async logAttack(
        attackType: string,
        ip: string,
        payload?: string,
        endpoint?: string,
        blocked: boolean = false,
        autoBlocked: boolean = false
    ): Promise<void> {
        try {
            await addDoc(collection(db, 'attackLogs'), {
                attackType,
                ip,
                timestamp: serverTimestamp(),
                ...(payload && { payload }),
                ...(endpoint && { endpoint }),
                blocked,
                autoBlocked,
            });

            console.log('📝 Attack logged to Firebase:', attackType);
        } catch (error) {
            console.error('❌ Error logging attack:', error);
        }
    }

    /**
     * Log an admin action
     */
    private static async logAdminAction(
        adminId: string,
        action: string,
        target: string,
        reason?: string
    ): Promise<void> {
        try {
            await addDoc(collection(db, 'adminActions'), {
                adminId,
                action,
                target,
                timestamp: serverTimestamp(),
                ...(reason && { reason }),
            });
        } catch (error) {
            console.error('❌ Error logging admin action:', error);
        }
    }

    /**
     * Manual sync with Firebase
     */
    static async sync(): Promise<void> {
        console.log('🔄 Manual sync started...');
        await this.loadBlockedIPs();
        await this.loadSecurityEvents();
        await this.loadAttackLogs();
        this.cache.lastSync = new Date();
        console.log('✅ Manual sync completed');
    }

    /**
     * Get last sync time
     */
    static getLastSyncTime(): Date {
        return this.cache.lastSync;
    }

    /**
     * Cleanup: Remove listeners
     */
    static cleanup(): void {
        this.unsubscribers.forEach(unsubscribe => unsubscribe());
        this.unsubscribers = [];
        console.log('🧹 Firebase Security Service cleanup completed');
    }
}

export default FirebaseSecurityService;
